import React, { createContext, useCallback, useContext, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { isTauriMode } from '../../utils/environmentDetector';
import { applyOsChrome, applyTitleBarVar, WINDOW_CHROME_EVENT } from '../../utils/desktop/windowChrome';

const FullscreenContext = createContext();

export const useFullscreen = () => {
    const context = useContext(FullscreenContext);
    // Return default values if context is not available (outside provider)
    if (!context) {
        return {
            isFullscreen: false,
            toggleFullscreen: () => { },
            enterFullscreen: () => { },
            exitFullscreen: () => { }
        };
    }
    return context;
};

const getTauriWindow = async () => {
    if (!isTauriMode()) return null;
    try {
        const { getCurrentWindow } = await import('@tauri-apps/api/window');
        return getCurrentWindow();
    } catch (error) {
        console.warn('[Fullscreen] Tauri window unavailable:', error);
        return null;
    }
};

export const FullscreenProvider = ({ children }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const nativeReady = useRef(false);

    const toggleFullscreen = useCallback(() => {
        setIsFullscreen(prev => !prev);
    }, []);

    const enterFullscreen = useCallback(() => {
        setIsFullscreen(true);
    }, []);

    const exitFullscreen = useCallback(() => {
        setIsFullscreen(false);
    }, []);

    useLayoutEffect(() => {
        applyTitleBarVar(isFullscreen);
    }, [isFullscreen]);

    // Desktop and browser both follow Start in Full Screen. An empty setting stays windowed.
    useEffect(() => {
        let cancelled = false;
        const startFullscreen = localStorage.getItem('pos_startInFullscreen') === 'true';
        (async () => {
            const win = await getTauriWindow();
            applyOsChrome(startFullscreen);
            if (win) {
                if (!cancelled) setIsFullscreen(startFullscreen);
                nativeReady.current = true;
                return;
            }
            if (startFullscreen) {
                setTimeout(() => {
                    if (!cancelled) setIsFullscreen(true);
                }, 500);
            }
            nativeReady.current = true;
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!nativeReady.current) return;
        applyOsChrome(isFullscreen);
    }, [isFullscreen]);

    useEffect(() => {
        const onChrome = () => applyOsChrome(isFullscreen);
        window.addEventListener(WINDOW_CHROME_EVENT, onChrome);
        return () => window.removeEventListener(WINDOW_CHROME_EVENT, onChrome);
    }, [isFullscreen]);

    const value = {
        isFullscreen,
        toggleFullscreen,
        enterFullscreen,
        exitFullscreen
    };

    return (
        <FullscreenContext.Provider value={value}>
            {children}
        </FullscreenContext.Provider>
    );
};
